/**
 * Security Interest Detail Panel — slide-out panel showing full details
 * for a selected security interest entry, with inline editing.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { titleCaseCompanyName } from "@cvx/lib/companyName";
import {
  X,
  FileText,
  ChevronRight,
  ChevronDown,
  Pencil,
  Check,
  Trash2,
  ArrowLeftRight,
  AlertTriangle,
  Info,
  EyeOff,
  Eye,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { cn } from "@/utils/misc";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
} from "../shared-panels/ChangeHistory";
import {
  SECURITY_INTEREST_FIELD_FORMATTERS,
  SECURITY_INTEREST_FIELD_LABELS,
} from "./historyFormatters";
import { SlideOverPanel } from "../shared-panels/SlideOverPanel";
import { NotesSection } from "../shared-panels/NotesSection";
import { LinkedRecordsSection } from "../shared-panels/LinkedRecordsSection";
import { SecuredPartyContactsSection } from "./SecuredPartyContactsSection";
import { SuggestedLinksSection } from "../shared-panels/SuggestedLinksSection";
import {
  ConfirmEditDialog,
  type PendingEdit,
} from "../shared-panels/ConfirmEditDialog";
import type { RecordType } from "../shared-panels/linkShared";
import { OwedEstimatesSection } from "./OwedEstimatesSection";
import { DocumentDetail } from "../../documents/DocumentDetail";
import type { SecurityInterestEntryUI } from "./types";
import { AddLiabilityPanel } from "../generalLiabilities/AddLiabilityPanel";
import {
  FlagDismissButton,
  FlagDismissEditor,
  flagGlowClass,
  type UseFlagDismissalsResult,
} from "../flags";

// ---------------------------------------------------------------------------
// Labels / options
// ---------------------------------------------------------------------------

const COLLATERAL_CLASS_LABELS: Record<string, string> = {
  motor_vehicle: "Motor vehicle",
  allpap_no_exceptions: "ALLPAP (no exceptions)",
  allpap_with_exceptions: "ALLPAP (with exceptions)",
  other_goods: "Other goods",
  account: "Account",
  intangible_property: "Intangible property",
  financial_property: "Financial property",
  chattel_paper: "Chattel paper",
  general_intangible: "General intangible",
  inventory: "Inventory",
  negotiable_instrument: "Negotiable instrument",
};

const FLAG_LABELS: Record<string, string> = {
  related_party: "Related party",
  registered_within_6_months: "6 month window",
  late_perfection: "Late perfection",
  unperfected: "Unperfected — voidable",
};

const FLAG_TOOLTIPS: Record<string, string> = {
  related_party: "Requires further investigation",
  registered_within_6_months:
    "Registered in relation back period. Potentially voidable under s588FJ of Corporations Act.",
  late_perfection:
    "Registered >20 business days after agreement — s 588FL Corporations Act",
  unperfected: "Voidable under s 267/267A PPSA",
};

const PARTY_TYPE_OPTIONS: Record<string, string> = {
  related_party: "Related party",
  third_party: "Third party",
};

const COLLATERAL_TYPE_OPTIONS: Record<string, string> = {
  commercial_property: "Commercial property",
  consumer_property: "Consumer property",
};

const FIXED_FLOATING_OPTIONS: Record<string, string> = {
  fixed: "Fixed",
  floating: "Floating",
};

const UNPERFECTED_REASON_OPTIONS: Record<string, string> = {
  no_ppsr_registration: "No PPSR registration",
  registration_expired: "Registration expired",
  registration_defective: "Registration defective",
  wrong_collateral_class: "Wrong collateral class",
};

const BOOL_OPTIONS: Record<string, string> = {
  true: "Yes",
  false: "No",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDate(d?: string): string {
  if (!d) return "\u2014";
  try {
    return new Date(d).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

function fmtAddr(a: {
  line1: string;
  suburb: string;
  state: string;
  postcode: string;
}): string {
  return `${a.line1}, ${a.suburb} ${a.state} ${a.postcode}`;
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function FlagBadge({
  flag,
  dismissed,
}: {
  flag: { type: string; severity: string; detail?: string };
  dismissed?: boolean;
}) {
  const colorMap: Record<string, string> = {
    critical:
      "bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800",
    warning:
      "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800",
    info: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-800",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded border px-2 py-0.5 text-xs font-medium",
        colorMap[flag.severity] ?? colorMap.info,
        dismissed ? "line-through opacity-40" : flagGlowClass(true),
      )}
      title={FLAG_TOOLTIPS[flag.type] ?? flag.type}
    >
      {FLAG_LABELS[flag.type] ?? flag.type}
    </span>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="pb-1.5 pt-6 text-xs font-semibold uppercase tracking-widest text-[#121212]">
      {children}
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
  multiline,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
  multiline?: boolean;
}) {
  return (
    <div className="py-2">
      <span className="mb-0.5 block text-sm text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          "block text-sm text-foreground",
          mono && "font-mono",
          multiline && "whitespace-pre-line",
        )}
      >
        {value || "\u2014"}
      </span>
    </div>
  );
}

function EditableField({
  label,
  value,
  field,
  onSave,
  isViewer,
  mono,
}: {
  label: string;
  value?: string | null;
  field: string;
  onSave: (f: string, v: string | undefined) => void;
  isViewer?: boolean;
  mono?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));

  const handleSave = () => {
    onSave(field, draft || undefined);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="py-2">
        <span className="mb-1 block text-sm text-muted-foreground">
          {label}
        </span>
        <div className="flex items-center gap-1">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-8 flex-1 text-sm"
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            autoFocus
          />
          <button
            onClick={handleSave}
            className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group py-2">
      <span className="mb-0.5 block text-sm text-muted-foreground">
        {label}
      </span>
      <div className="flex items-center gap-1.5">
        <span className={cn("text-sm text-foreground", mono && "font-mono")}>
          {value || "\u2014"}
        </span>
        {!isViewer && (
          <button
            onClick={() => {
              setDraft(String(value ?? ""));
              setEditing(true);
            }}
            className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:bg-muted group-hover:text-muted-foreground"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  field,
  onSave,
  isViewer,
  stacked,
}: {
  label: string;
  value: string;
  options: Record<string, string>;
  field: string;
  onSave: (f: string, v: string) => void;
  isViewer?: boolean;
  /** @deprecated all selects now stack the label above a full-width control. */
  stacked?: boolean;
}) {
  void stacked;
  if (isViewer) {
    return (
      <div className="py-2">
        <span className="mb-0.5 block text-sm text-muted-foreground">
          {label}
        </span>
        <span className="text-sm text-foreground">
          {options[value] ?? value}
        </span>
      </div>
    );
  }
  return (
    <div className="py-2">
      <span className="mb-1 block text-sm text-muted-foreground">{label}</span>
      <Select value={value} onValueChange={(v) => onSave(field, v)}>
        <SelectTrigger className="h-8 w-full text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(options).map(([k, v]) => (
            <SelectItem key={k} value={k}>
              {v}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function BoolSelectField({
  label,
  value,
  field,
  onSave,
  isViewer,
}: {
  label: string;
  value: boolean;
  field: string;
  onSave: (f: string, v: boolean) => void;
  isViewer?: boolean;
}) {
  if (isViewer) {
    return (
      <div className="py-2">
        <span className="mb-0.5 block text-sm text-muted-foreground">
          {label}
        </span>
        <span className="text-sm text-foreground">{value ? "Yes" : "No"}</span>
      </div>
    );
  }
  return (
    <div className="py-2">
      <span className="mb-1 block text-sm text-muted-foreground">{label}</span>
      <Select
        value={String(value)}
        onValueChange={(v) => onSave(field, v === "true")}
      >
        <SelectTrigger className="h-8 w-full text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(BOOL_OPTIONS).map(([k, v]) => (
            <SelectItem key={k} value={k}>
              {v}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface SecurityInterestDetailPanelProps {
  entry: SecurityInterestEntryUI | null;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
  isViewer?: boolean;
  flagCtx: UseFlagDismissalsResult;
  placement?: "primary" | "secondary";
  /** Position in the secondary panel stack (1 = closest to primary). */
  stackIndex?: number;
  onOpenLinked?: (type: RecordType, id: string) => void;
}

export function SecurityInterestDetailPanel({
  entry,
  open,
  onClose,
  onDeleted,
  isViewer,
  flagCtx,
  placement = "primary",
  stackIndex,
  onOpenLinked,
}: SecurityInterestDetailPanelProps) {
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  // Edit awaiting practitioner confirmation (all inline edits pass through
  // the shared ConfirmEditDialog before any mutation fires).
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null);

  const updateField = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.securityInterests.publicMutations
        .updateSecurityInterestField,
    ),
  });
  const toggleExcluded = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.securityInterests.publicMutations
        .toggleExcluded,
    ),
  });
  const deleteEntry = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.securityInterests.publicMutations
        .deleteSecurityInterest,
    ),
  });
  useEffect(() => {
    if (open) {
      setConfirmDelete(false);
      setConvertOpen(false);
    }
  }, [open]);

  const historyUserIds = useMemo(() => {
    if (!entry?.changeHistory) return [] as Id<"users">[];
    const seen = new Set<Id<"users">>();
    for (const h of entry.changeHistory) {
      const uid = (h as { userId?: Id<"users"> }).userId;
      if (uid) seen.add(uid);
    }
    return Array.from(seen);
  }, [entry?.changeHistory]);

  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, {
      ids: historyUserIds,
    }),
    enabled: historyUserIds.length > 0,
  });

  const usersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) {
      map.set(u._id, u);
    }
    return map;
  }, [historyUsers]);

  if (!entry) return null;

  // Stages the edit behind the shared confirmation dialog — the mutation only
  // fires once the practitioner confirms (optionally leaving a note).
  const handleFieldSave = (field: string, value: unknown) => {
    setPendingEdit({
      fieldLabel: SECURITY_INTEREST_FIELD_LABELS[field] ?? field,
      onConfirm: (note) =>
        updateField.mutateAsync({
          entryId: entry._id as Id<"securityInterestEntries">,
          field,
          value,
          note,
        }),
    });
  };

  const handleDelete = () => {
    deleteEntry.mutate(
      { entryId: entry._id as Id<"securityInterestEntries"> },
      {
        onSuccess: () => {
          onDeleted?.();
          onClose();
        },
      },
    );
  };

  const sp = entry.securedParty;
  const isRegistered = entry.registrationStatus === "registered";
  const physAddr = sp.addresses?.find((a) => a.type === "physical");
  const mailAddr = sp.addresses?.find((a) => a.type === "mailing");

  return (
    <>
      <SlideOverPanel
        open={open}
        onClose={onClose}
        placement={placement}
        stackIndex={stackIndex}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 border-b bg-background/95 px-5 pb-3 pt-5 backdrop-blur-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {/* Editable secured party name */}
              {!isViewer && editingName ? (
                <div className="mt-0.5 flex items-center gap-1.5">
                  <Input
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    className="h-8 text-lg font-semibold"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleFieldSave("securedParty.name", nameDraft);
                        setEditingName(false);
                      }
                      if (e.key === "Escape") setEditingName(false);
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      handleFieldSave("securedParty.name", nameDraft);
                      setEditingName(false);
                    }}
                    className="p-1 text-emerald-600"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setEditingName(false)}
                    className="p-1 text-muted-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div
                  className={cn("group", !isViewer && "cursor-pointer")}
                  onClick={() => {
                    if (!isViewer) {
                      setNameDraft(sp.name);
                      setEditingName(true);
                    }
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    <h2 className="mt-0.5 text-lg font-semibold leading-snug text-foreground">
                      {titleCaseCompanyName(sp.name)}
                    </h2>
                    {!isViewer && (
                      <Pencil className="mt-1 h-3.5 w-3.5 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
                    )}
                  </div>
                </div>
              )}

              <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                {sp.identifierType} {sp.identifier}
              </div>

              <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                {isRegistered
                  ? `PPSR #${entry.ppsrRegistrationNumber ?? "—"}`
                  : "UNPERFECTED"}
              </div>
            </div>

            <div className="mt-0.5 flex shrink-0 items-center gap-1">
              {!isViewer && (
                <button
                  onClick={() => setConvertOpen(true)}
                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  title="Convert to general liability"
                >
                  <ArrowLeftRight className="h-[16px] w-[16px]" />
                </button>
              )}
              {!isViewer && (
                <button
                  onClick={() =>
                    toggleExcluded.mutate({
                      entryId: entry._id as Id<"securityInterestEntries">,
                      isExcluded: !entry.isExcluded,
                    })
                  }
                  className={cn(
                    "rounded-lg p-1.5 transition-colors hover:bg-muted",
                    entry.isExcluded
                      ? "text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  title={
                    entry.isExcluded
                      ? "Include in totals"
                      : "Exclude from totals"
                  }
                >
                  {entry.isExcluded ? (
                    <Eye className="h-[16px] w-[16px]" />
                  ) : (
                    <EyeOff className="h-[16px] w-[16px]" />
                  )}
                </button>
              )}
              {!isViewer && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30"
                  title="Delete"
                >
                  <Trash2 className="h-[16px] w-[16px]" />
                </button>
              )}
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-[18px] w-[18px]" />
              </button>
            </div>
          </div>

          {entry.flags.length > 0 && (
            <div className="mt-3">
              <div className="flex flex-wrap gap-1.5">
                {entry.flags.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-0.5">
                    <FlagBadge
                      flag={f}
                      dismissed={flagCtx.isDismissed(
                        "securityInterests",
                        entry._id,
                        { type: f.type, detail: f.detail ?? "" },
                      )}
                    />
                    <FlagDismissButton
                      ctx={flagCtx}
                      agentKind="securityInterests"
                      entryId={entry._id}
                      flagType={f.type}
                      flagDetail={f.detail ?? ""}
                    />
                  </span>
                ))}
              </div>
              {entry.flags.map((f, i) => (
                <FlagDismissEditor
                  key={i}
                  ctx={flagCtx}
                  agentKind="securityInterests"
                  entryId={entry._id}
                  entryLabel={sp.name}
                  flagType={f.type}
                  flagLabel={FLAG_LABELS[f.type] ?? f.type}
                  flagDetail={f.detail ?? ""}
                />
              ))}
            </div>
          )}
        </div>

        {/* Excluded notice */}
        {entry.isExcluded && (
          <div className="mx-5 mt-4 flex items-start gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2">
            <EyeOff className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <p className="text-xs leading-relaxed text-muted-foreground">
              This security interest is excluded — it stays in the register but
              is left out of the secured-creditor totals, secured netting, the
              Proof of Debt process and reporting.
            </p>
          </div>
        )}

        {/* Delete confirmation */}
        {confirmDelete && (
          <div className="mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
            <p className="text-sm text-red-700 dark:text-red-300">
              Remove this security interest?
            </p>
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteEntry.isPending}
              >
                {deleteEntry.isPending ? "Removing..." : "Remove"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="px-5 pb-8">
          {/* Registration (for registered entries) */}
          {isRegistered && (
            <>
              <SectionHeader>Registration</SectionHeader>
              <EditableField
                label="PPSR registration #"
                value={entry.ppsrRegistrationNumber}
                field="ppsrRegistrationNumber"
                onSave={handleFieldSave}
                isViewer={isViewer}
                mono
              />
              <EditableField
                label="Giving of notice identifier"
                value={entry.givingOfNoticeIdentifier}
                field="givingOfNoticeIdentifier"
                onSave={handleFieldSave}
                isViewer={isViewer}
                mono
              />
              <div className="grid grid-cols-2 gap-x-4">
                <EditableField
                  label="Registration start"
                  value={
                    entry.registrationStartDate
                      ? fmtDate(entry.registrationStartDate)
                      : undefined
                  }
                  field="registrationStartDate"
                  onSave={handleFieldSave}
                  isViewer={isViewer}
                />
                {entry.registrationEndDate && (
                  <EditableField
                    label="Registration end"
                    value={fmtDate(entry.registrationEndDate)}
                    field="registrationEndDate"
                    onSave={handleFieldSave}
                    isViewer={isViewer}
                  />
                )}
              </div>
              <EditableField
                label="Security agreement date"
                value={
                  entry.agreementDate ? fmtDate(entry.agreementDate) : undefined
                }
                field="agreementDate"
                onSave={handleFieldSave}
                isViewer={isViewer}
              />
              <div className="mt-2 border-b" />
            </>
          )}

          {/* Agreement (for unperfected entries) */}
          {!isRegistered && (
            <>
              <SectionHeader>Agreement</SectionHeader>
              <EditableField
                label="Security agreement date"
                value={
                  entry.agreementDate ? fmtDate(entry.agreementDate) : undefined
                }
                field="agreementDate"
                onSave={handleFieldSave}
                isViewer={isViewer}
              />
              <SelectField
                label="Reason unperfected"
                value={entry.unperfectedReason ?? "no_ppsr_registration"}
                options={UNPERFECTED_REASON_OPTIONS}
                field="unperfectedReason"
                onSave={handleFieldSave}
                isViewer={isViewer}
              />
              {entry.unperfectedReasonDetail && (
                <EditableField
                  label="Detail"
                  value={entry.unperfectedReasonDetail}
                  field="unperfectedReasonDetail"
                  onSave={handleFieldSave}
                  isViewer={isViewer}
                />
              )}
              <div className="mt-2 border-b" />
            </>
          )}

          {/* Owed amount — estimates list, mirroring asset valuations */}
          <SectionHeader>Estimated amount owed</SectionHeader>
          <OwedEstimatesSection entry={entry} isViewer={isViewer} />
          <div className="mt-4 border-b" />

          {/* Flag details — collapsible cards mirroring the asset panel flag styling */}
          {entry.flags.length > 0 && (
            <>
              <SectionHeader>Flag details</SectionHeader>
              <div className="space-y-2 pt-1">
                {entry.flags.map((f, i) => {
                  const isWarning =
                    f.severity === "warning" || f.severity === "critical";
                  return (
                    <Collapsible key={i} defaultOpen>
                      <div
                        className={cn(
                          "overflow-hidden rounded-lg border",
                          isWarning
                            ? "border-rose-200 bg-rose-50/50 dark:border-rose-800 dark:bg-rose-950/20"
                            : "border-sky-200 bg-sky-50/50 dark:border-sky-800 dark:bg-sky-950/20",
                        )}
                      >
                        <CollapsibleTrigger className="group flex w-full items-center gap-3 px-3 py-2.5 text-left">
                          {isWarning ? (
                            <AlertTriangle className="h-4 w-4 shrink-0 text-rose-500" />
                          ) : (
                            <Info className="h-4 w-4 shrink-0 text-sky-500" />
                          )}
                          <span
                            className={cn(
                              "flex-1 text-xs font-semibold uppercase tracking-wider",
                              isWarning
                                ? "text-rose-700 dark:text-rose-400"
                                : "text-sky-700 dark:text-sky-400",
                            )}
                          >
                            {FLAG_LABELS[f.type] ?? f.type}
                          </span>
                          <ChevronDown
                            className={cn(
                              "h-3.5 w-3.5 shrink-0 transition-transform",
                              isWarning
                                ? "text-rose-400 dark:text-rose-600"
                                : "text-sky-400 dark:text-sky-600",
                              "group-data-[state=open]:rotate-180",
                            )}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="px-3 pb-2.5">
                            <div className="flex items-start gap-1.5 pl-7">
                              <p
                                className={cn(
                                  "flex-1 whitespace-pre-line text-sm text-muted-foreground",
                                  flagCtx.isDismissed(
                                    "securityInterests",
                                    entry._id,
                                    {
                                      type: f.type,
                                      detail: f.detail ?? "",
                                    },
                                  ) && "line-through opacity-40",
                                )}
                              >
                                {f.detail ?? "Flagged for review"}
                              </p>
                              <FlagDismissButton
                                ctx={flagCtx}
                                agentKind="securityInterests"
                                entryId={entry._id}
                                flagType={f.type}
                                flagDetail={f.detail ?? ""}
                                className="mt-0.5 shrink-0"
                              />
                            </div>
                            <FlagDismissEditor
                              ctx={flagCtx}
                              agentKind="securityInterests"
                              entryId={entry._id}
                              entryLabel={sp.name}
                              flagType={f.type}
                              flagLabel={FLAG_LABELS[f.type] ?? f.type}
                              flagDetail={f.detail ?? ""}
                            />
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  );
                })}
              </div>
              <div className="mt-2 border-b" />
            </>
          )}

          {/* Party type */}
          <SectionHeader>Party type</SectionHeader>
          <SelectField
            label="Classification"
            value={entry.partyType}
            options={PARTY_TYPE_OPTIONS}
            field="partyType"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <div className="mt-2 border-b" />

          {/* Collateral */}
          <SectionHeader>Collateral</SectionHeader>
          <SelectField
            label="Type"
            value={entry.collateralType}
            options={COLLATERAL_TYPE_OPTIONS}
            field="collateralType"
            onSave={handleFieldSave}
            isViewer={isViewer}
            stacked
          />
          <SelectField
            label="Class"
            value={entry.collateralClass}
            options={COLLATERAL_CLASS_LABELS}
            field="collateralClass"
            onSave={handleFieldSave}
            isViewer={isViewer}
            stacked
          />

          {isRegistered && (
            <>
              <SelectField
                label="Fixed / floating"
                value={entry.fixedOrFloating}
                options={FIXED_FLOATING_OPTIONS}
                field="fixedOrFloating"
                onSave={handleFieldSave}
                isViewer={isViewer}
              />
              <BoolSelectField
                label="Proceeds"
                value={entry.proceeds}
                field="proceeds"
                onSave={handleFieldSave}
                isViewer={isViewer}
              />
              <BoolSelectField
                label="Inventory"
                value={entry.inventory}
                field="inventory"
                onSave={handleFieldSave}
                isViewer={isViewer}
              />
              <BoolSelectField
                label="PMSI"
                value={entry.pmsi}
                field="pmsi"
                onSave={handleFieldSave}
                isViewer={isViewer}
              />
            </>
          )}

          {/* Collateral description — collapsible markdown with edit */}
          <NotesSection
            label="Description"
            value={entry.collateralDescription ?? ""}
            onSave={(v) => handleFieldSave("collateralDescription", v)}
            isViewer={isViewer}
            emptyLabel="No description"
          />

          <div className="mt-2 border-b" />

          {/* Address */}
          {(physAddr || mailAddr) && (
            <>
              <SectionHeader>Secured party address</SectionHeader>
              {physAddr && (
                <DetailRow label="Physical" value={fmtAddr(physAddr)} />
              )}
              {mailAddr && (
                <DetailRow label="Mailing" value={fmtAddr(mailAddr)} />
              )}
              <div className="mt-2 border-b" />
            </>
          )}

          {/* Practitioner notes */}
          <NotesSection
            value={entry.notes ?? ""}
            onSave={(v) => handleFieldSave("notes", v)}
            isViewer={isViewer}
          />
          <div className="mt-4 border-b" />

          {/* Source documents */}
          {entry.sourceDocuments && entry.sourceDocuments.length > 0 && (
            <Collapsible defaultOpen>
              <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 pb-1.5 pt-6 text-left text-xs font-semibold uppercase tracking-widest text-[#121212]">
                <span>Source documents</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="flex flex-col gap-2 pt-2.5">
                  {entry.sourceDocuments.map((doc, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() =>
                        setViewingDocumentId(doc.documentId as Id<"documents">)
                      }
                      className="group flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {doc.fileName}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </CollapsibleContent>
              <div className="mt-4 border-b" />
            </Collapsible>
          )}

          {/* Linked records */}
          {onOpenLinked && (
            <div className="mt-4 space-y-4">
              <SuggestedLinksSection
                jobId={entry.jobId}
                anchor={{ type: "security_interest", id: entry._id }}
                onOpenLinked={onOpenLinked}
                isViewer={isViewer}
              />
              <LinkedRecordsSection
                jobId={entry.jobId}
                anchor={{ type: "security_interest", id: entry._id }}
                onOpenLinked={onOpenLinked}
                isViewer={isViewer}
              />
            </div>
          )}

          {/* Secured party contacts (lead + joint parties, MOR-708) */}
          <SecuredPartyContactsSection
            jobId={entry.jobId as Id<"jobs">}
            entryId={entry._id as Id<"securityInterestEntries">}
            isViewer={isViewer}
          />

          {/* History */}
          {entry.changeHistory && (
            <div className="mt-4">
              <ChangeHistory
                history={entry.changeHistory}
                usersById={usersById}
                formatters={SECURITY_INTEREST_FIELD_FORMATTERS}
                fieldLabels={SECURITY_INTEREST_FIELD_LABELS}
                persistId="security-interest-detail:history"
              />
            </div>
          )}
        </div>
      </SlideOverPanel>

      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />

      {/* Convert -> Liability sheet */}
      {!isViewer && (
        <AddLiabilityPanel
          jobId={entry.jobId}
          taskId={entry.taskId}
          open={convertOpen}
          onClose={() => setConvertOpen(false)}
          onCreated={() => {
            setConvertOpen(false);
            onDeleted?.();
            onClose();
          }}
          convertFromSecurityInterestId={
            entry._id as Id<"securityInterestEntries">
          }
          prefill={{
            creditorName: entry.securedParty.name,
            amount: entry.owedAmount,
            natureOfDebt: "loan",
            source: "other",
            secured: true,
            securityDetails: entry.collateralDescription,
            date: entry.owedAmountAsAt,
            notes: entry.notes,
          }}
        />
      )}

      {/* Confirmation gate for every inline edit in this panel */}
      <ConfirmEditDialog
        edit={pendingEdit}
        onClose={() => setPendingEdit(null)}
      />
    </>
  );
}
